/**
 * Клас для роботи з REST API
 * 
 * @package AGDS
 * @category Modules
 * @module ApiService
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
class ApiService {

	/**
	 * Конструктор
	 * 
	 * @param {string} baseUrl - Базова URL-адреса API
	 * @param {string} nonce - Нонс
	 */
	constructor(baseUrl, nonce) {
		this.baseUrl = baseUrl
		this.nonce = nonce
	}

	/**
	 * Універсальний метод для GET-запитів
	 * 
	 * @param {string} endpoint - URL-адреса запиту
	 * @param {object} params - Параметри запиту
	 * @returns {Promise<object>} - Об'єкт відповіді
	 */
	async get(endpoint, params = {}) {
		// endpoint може бути відносним, baseUrl має містити слеш вкінці — перевірте при ініціалізації
		const url = new URL(this.baseUrl + endpoint)

		Object.entries(params || {}).forEach(([key, value]) => {
			if (value === undefined || value === null) return

			// підтримка масивів і звичайних значень
			if (Array.isArray(value)) {
				value.forEach(v => url.searchParams.append(key, String(v)))
			} else {
				url.searchParams.append(key, String(value))
			}
		})

		const options = {
			method: 'GET',
			headers: {
				'X-WP-Nonce': this.nonce,
				'X-Requested-With': 'XMLHttpRequest'
			}
		}

		return this.request(url, options)
	}

	/**
	 * Універсальний метод для POST-запитів
	 * 
	 * @param {string} endpoint - URL-адреса запиту
	 * @param {object} body - Тіло запиту
	 * @returns {Promise<object>} - Об'єкт відповіді
	 */
	async post(endpoint, body = {}) {
		const url = new URL(this.baseUrl + endpoint)

		const options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': this.nonce,
				'X-Requested-With': 'XMLHttpRequest'
			},
			body: JSON.stringify(body),
		}

		return this.request(url, options)
	}

	/**
	 * Приватний метод для виконання fetch та обробки помилок
	 * 
	 * @param {string} url - URL-адреса запиту
	 * @param {object} options - Параметри запиту
	 * @returns {Promise<object>} - Об'єкт відповіді
	 */
	async request(url, options = {}) {
		try {
			// переконаємось, що передаємо рядок у глобальний fetch
			const response = await fetch(url.toString(), options)

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}))
				const message = errorData.message || `HTTP error! Status: ${response.status}`
				throw new Error(message)
			}

			return await response.json()
		} catch (error) {
			console.error('API Service Error:', error)
			return { html: '', has_more: false }
		}
	}

	/**
	 * Отримує пости (для "Load More") - використовує GET
	 * 
	 * @param {string} postType - Тип поста
	 * @param {number} page - Поточна сторінка
	 * @param {object} extraParams - Додаткові параметри
	 * @returns {Promise<object>}
	 */
	postsLoadMore(postType, page, extraParams = {}) {
		const params = Object.assign({ post_type: postType, page: page }, extraParams)
		return this.get('posts-loader', params)
	}

	/**
	 * Отримує пости (для "Filter") - використовує POST
	 * 
	 * @param {string} postType - Тип поста
	 * @param {number} page - Сторінка для завантаження
	 * @param {object} filters - Фільтри
	 * @returns {Promise<object>}
	 */
	postsFilter(postType, page, filters = {}) {
		return this.post('posts-loader', {
			post_type: postType,
			page: page,
			filters: filters,
		})
	}
}

// Singleton
export const api = new ApiService(agds.rest.url, agds.rest.nonce)